/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { TextField, Button, IconButton } from "@mui/material";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import { SimplicityComposerEnsureTipTap } from "cms/rich-text/simplicity-composer-tiptap";
import LabelledField from "components/labelled-field";
import { useTranslation } from "react-i18next";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { LinkSelectorWrapper } from "cms/links/link-selector/link-selector-wrapper";
import { useEffect, useState } from "react";
import { isGuid } from "utils/empty-exists";
import { Logger } from "cms/utils/log/logger";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as TrashIcon } from "assets/images/icons/common/trash.svg";
import LinkButtonSelectOption from "cms/components/components/link-button/edit/link-button-select-style";
import { colourOptions, defaultOptions, iconOptions, styleOptions } from "cms/components/components/link-button/constants";

const ButtonCTAComponentEditor = () => {
    const { t } = useTranslation();

    const { token } = useAuth();
    const { component, update, updateFactory } = useUpdateComponent();
    const title = component?.title;
    const subtitle = component?.subtitle;
    const description = component?.description;
    const links = component?.links || [];
    const linkProperties = component?.linkProperties || {};

    const [linksData, setLinksData] = useState([]);
    const [loadingLinks, setLoadingLinks] = useState([]);
    const [linkObjects, setLinkObjects] = useState([]); // Store full link objects
    
    const { defaultStyle, defaultColour, defaultIcon } = defaultOptions;

    
    const onChangeTitle = (event) => {
        const title = event.target.value;
        update("title", title);
    };
    
    const onChangeSubtitle = (event) => {
        const subtitle = event.target.value;
        update("subtitle", subtitle);
    };
    
    const onChangeDescription = updateFactory("description");
    
    const onChangeLink = (link, index) => {
        const newLinksData = [...linksData];
        const { guid } = link;
        newLinksData[index] = {
            link: guid,
            colour: defaultColour,
            style: defaultStyle,
            icon: defaultIcon
        };
        setLinksData(newLinksData);
        
        // Store the full link object
        const newLinkObjects = [...linkObjects];
        newLinkObjects[index] = link;
        setLinkObjects(newLinkObjects);
        
        // Update the component's links array with just the guid
        const newLinks = [...links];
        newLinks[index] = guid;
        update("links", newLinks);
        
        // Ensure linkProperties exists and update the component's link properties using guid as key
        const currentLinkProperties = component?.linkProperties || {};
        const existingProps = currentLinkProperties[guid] || {};
        update("linkProperties", {
            ...currentLinkProperties,
            [guid]: {
                colour: existingProps.colour || defaultColour,
                style: existingProps.style || defaultStyle,
                icon: existingProps.icon || defaultIcon
            }
        });
    };
    
    const onChangeStyle = (event, index) => {
        const newStyle = event?.target?.value;
        const newLinksData = [...linksData];
        newLinksData[index] = {
            ...newLinksData[index],
            style: newStyle
        };
        setLinksData(newLinksData);
        
        // Update the component's link properties using guid as key
        const linkGuid = links[index];
        if (linkGuid) {
            // Ensure linkProperties exists and update the specific property
            const currentLinkProperties = component?.linkProperties || {};
            const currentProps = currentLinkProperties[linkGuid] || {};
            update("linkProperties", {
                ...currentLinkProperties,
                [linkGuid]: {
                    ...currentProps,
                    style: newStyle
                }
            });
        }
    };
    
    const onChangeColour = (event, index) => {
        const newColour = event?.target?.value;
        const newLinksData = [...linksData];
        newLinksData[index] = {
            ...newLinksData[index],
            colour: newColour
        };
        setLinksData(newLinksData);
        
        // Update the component's link properties using guid as key
        const linkGuid = links[index];
        if (linkGuid) {
            // Ensure linkProperties exists and update the specific property
            const currentLinkProperties = component?.linkProperties || {};
            const currentProps = currentLinkProperties[linkGuid] || {};
            update("linkProperties", {
                ...currentLinkProperties,
                [linkGuid]: {
                    ...currentProps,
                    colour: newColour
                }
            });
        }
    };
    
    const onChangeIcon = (event, index) => {
        const newIcon = event?.target?.value;
        const newLinksData = [...linksData];
        newLinksData[index] = {
            ...newLinksData[index],
            icon: newIcon
        };
        setLinksData(newLinksData);
        
        // Update the component's link properties using guid as key
        const linkGuid = links[index];
        if (linkGuid) {
            // Ensure linkProperties exists and update the specific property
            const currentLinkProperties = component?.linkProperties || {};
            const currentProps = currentLinkProperties[linkGuid] || {};
            update("linkProperties", {
                ...currentLinkProperties,
                [linkGuid]: {
                    ...currentProps,
                    icon: newIcon
                }
            });
        }
    };

    const onRemoveLink = (index) => {
        const newLinksData = [...linksData];
        newLinksData.splice(index, 1);
        setLinksData(newLinksData);
        
        const newLinks = [...links];
        newLinks.splice(index, 1);
        update("links", newLinks);
        
        const newLoadingLinks = [...loadingLinks];
        newLoadingLinks.splice(index, 1);
        setLoadingLinks(newLoadingLinks);
        
        const newLinkObjects = [...linkObjects];
        newLinkObjects.splice(index, 1);
        setLinkObjects(newLinkObjects);
    };

    const onAddLink = () => {
        const newLinksData = [...linksData, {
            link: undefined,
            colour: defaultColour,
            style: defaultStyle,
            icon: defaultIcon
        }];
        setLinksData(newLinksData);
        
        const newLinks = [...links, undefined];
        update("links", newLinks);
        
        const newLoadingLinks = [...loadingLinks, false];
        setLoadingLinks(newLoadingLinks);
        
        const newLinkObjects = [...linkObjects, undefined];
        setLinkObjects(newLinkObjects);
    };

    const loadLink = async (linkGuid) => {
        if (isGuid(linkGuid)) {
            const _link = await SimpliCityAdminSDK.link.findOne(token, linkGuid);
            return _link;
        }
    };

    useEffect(() => {
        const loadLinks = async () => {
            try {
                const newLoadingLinks = links.map(() => true);
                setLoadingLinks(newLoadingLinks);
                
                const loadedLinks = await Promise.all(
                    links.map((linkGuid) => loadLink(linkGuid))
                );
                
                // Convert loaded links to the new data structure
                const newLinksData = loadedLinks.map((link, index) => {
                    const linkGuid = links[index];
                    const storedProps = linkGuid ? (linkProperties[linkGuid] || {}) : {};
                    return {
                        link: linkGuid,
                        colour: storedProps.colour || defaultColour,
                        style: storedProps.style || defaultStyle,
                        icon: storedProps.icon || defaultIcon
                    };
                });
                
                setLinksData(newLinksData);
                setLinkObjects(loadedLinks);
                
                // Only set default properties for links that don't have properties yet
                const currentLinkProperties = component?.linkProperties || {};
                const updatedLinkProperties = { ...currentLinkProperties };
                
                links.forEach((linkGuid, index) => {
                    if (linkGuid && !updatedLinkProperties[linkGuid]) {
                        updatedLinkProperties[linkGuid] = {
                            colour: defaultColour,
                            style: defaultStyle,
                            icon: defaultIcon
                        };
                    }
                });
                
                update("linkProperties", updatedLinkProperties);
            } catch (error) {
                Logger.error(error);
            } finally {
                const newLoadingLinks = links.map(() => false);
                setLoadingLinks(newLoadingLinks);
            }
        };
        
        // load once if we have links and no link data loaded yet
        if (links.length > 0 && linksData.length === 0) {
            loadLinks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [links]);

    const setLoadingLinkAtIndex = (index, loading) => {
        const newLoadingLinks = [...loadingLinks];
        newLoadingLinks[index] = loading;
        setLoadingLinks(newLoadingLinks);
    };

    return (
        <div className="simplicity-editor-frame">
            {/*  Title */}
            <LabelledField label={`${t("commons.displayTitle")} :`}>
                <TextField
                    id="button_cta_title"
                    fullWidth={true}
                    value={title}
                    placeholder="Add a title"
                    onChange={onChangeTitle}
                />
            </LabelledField>
            
            {/*  Subtitle */}
            <LabelledField label="Subtitle :">
                <TextField
                    id="button_cta_subtitle"
                    fullWidth={true}
                    value={subtitle}
                    placeholder="Add a subtitle"
                    onChange={onChangeSubtitle}
                />
            </LabelledField>
            
            {/*  Description  */}
            <LabelledField label="Description :">
                <SimplicityComposerEnsureTipTap
                    richtext={description}
                    onChange={onChangeDescription}
                />
            </LabelledField>
            
            {/*  Links Selectors  */}
            <LabelledField label="Button Links :">
                <div className="button-cta-editor-links-container">
                    <Button
                        startIcon={<AddIcon />}
                        onClick={onAddLink}
                        variant="outlined"
                        size="small"
                        className="button-cta-editor-add-button"
                    >
                        Add Link
                    </Button>
                    {linksData.map((linkItem, index) => (
                        <div key={index} className="button-cta-editor-link-item">
                            <div className="button-cta-editor-link-selector">
                                <LinkSelectorWrapper
                                    onLinkChange={(link) => onChangeLink(link, index)}
                                    onLinkRemove={() => onRemoveLink(index)}
                                    link={linkObjects[index]}
                                    setLink={(link) => {
                                        const newLinkObjects = [...linkObjects];
                                        newLinkObjects[index] = link;
                                        setLinkObjects(newLinkObjects);
                                        
                                        if (link) {
                                            const newLinksData = [...linksData];
                                            newLinksData[index] = {
                                                ...newLinksData[index],
                                                link: link.guid
                                            };
                                            setLinksData(newLinksData);
                                        }
                                    }}
                                    loadingLink={loadingLinks[index]}
                                    setLoadingLink={(loading) => setLoadingLinkAtIndex(index, loading)}
                                    disabled={false}
                                />

                                <IconButton
                                    onClick={() => onRemoveLink(index)}
                                    size="small"
                                    color="error"
                                    className="button-cta-editor-remove-button"
                                >
                                    <TrashIcon />
                                </IconButton>
                            </div>
                            {linkItem?.link && (
                                <>
                                    <LinkButtonSelectOption
                                        label='Style'
                                        onChange={(event) => onChangeStyle(event, index)}
                                        options={styleOptions}
                                        value={linkItem.style}
                                    />
                                    <LinkButtonSelectOption
                                        label='Colour'
                                        onChange={(event) => onChangeColour(event, index)}
                                        options={colourOptions}
                                        value={linkItem.colour}
                                    />
                                    <LinkButtonSelectOption
                                        label='Icon'
                                        onChange={(event) => onChangeIcon(event, index)}
                                        options={iconOptions}
                                        value={linkItem.icon}
                                    />
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </LabelledField>
        </div>
    );
};

ButtonCTAComponentEditor.displayName = "ButtonCTAComponentEditor";
export { ButtonCTAComponentEditor };
